import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { ActivatedRoute, Router, ParamMap } from '@angular/router'
import { Observable, Subscription } from 'rxjs'
import { Article, selectAnArticleById, selectAnArticleByRunningOrder, selectCurrentArticle, setCurrentArticle } from '../../../state'
import { IconNamesEnum } from 'ngx-bootstrap-icons'
import { BaseDisplayComponent } from '../display-component-base/display-component-base'
import { Dictionary } from '@ngrx/entity'
import { MixpanelService } from 'src/app/services/mixpanel.service'
import { selectUser } from 'src/app/state/user.reducer'

@Component({
    template: '',
    standalone: true
})
export abstract class BaseArticleComponent<TTItem extends Article> extends BaseDisplayComponent implements OnInit, OnDestroy {
    route = inject(ActivatedRoute)
    store = inject<Store<TTItem>>(Store)
    protected mixpanel = inject(MixpanelService)

    public area = ''

    @Input() articleID = 0

    public currentSection = 'top'
    public activeTab = 1

    public routeChangeSub$: Subscription = Subscription.EMPTY
    public article$: Observable<TTItem>
    public articleSub$: Subscription = Subscription.EMPTY
    public user$: Subscription = Subscription.EMPTY
    public iconNames = IconNamesEnum
    protected sections: string[] = []

    public downloadAction: Dictionary<any> = {}

    public followedArticles: Dictionary<number> = {}

    constructor() {
        super()
        const route = inject(ActivatedRoute)

        this.area = route.snapshot.data['contentSet']

        this.articleID = parseInt(this.route.snapshot.paramMap.get('id') ?? '')
        this.article$ = this.store.select(selectCurrentArticle()) as unknown as Observable<TTItem>
        this.store.dispatch(setCurrentArticle({
            id: this.articleID
        }))
    }

    public startDownload(articleID: number) {
        this.downloadAction = {
            export: 'xls',
            title: this.area.charAt(0).toUpperCase() + this.area.slice(1),
            area: this.area + '/download/article',
            parameters: { articleID: articleID.toString() },
            columns: [],
            order: [],
        }
    }

    onSectionChange(sectionId: string) {
        this.currentSection = sectionId
    }

    scrollTo(section: string) {
        document.querySelector('#' + section)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        })
        this.currentSection = section
    }

    ngOnInit() {
        this.routeChangeSub$ = this.route.paramMap
            .subscribe(map => {
                this.activeTab = 1
                this.scrollTo('top')
                return this.getRouteParams(map)
            })

        this.user$ = this.store.select(selectUser).subscribe(user => {
            this.followedArticles = {}
            for (const searchesForArea of Object.values(user?.searches ?? {})) {
                for (const search of searchesForArea) {
                    if (search.parameters.id ?? 0 > 0) {
                        this.followedArticles[search.parameters.id] = search
                    }
                }

            }
        })
    }

    ngOnDestroy() {
        this.routeChangeSub$.unsubscribe()
        this.articleSub$.unsubscribe()
        this.user$.unsubscribe()
    }

    getRouteParams(map: ParamMap): number {
        let onPrintPage = false
        let articleID = parseInt(map.get('id') ?? '')
        if (isNaN(articleID)) {
            articleID = this.articleID
            onPrintPage = true
        }
        let id: number = -1
        if (articleID) {
            id = articleID
            this.store.dispatch({ type: '[Articles] Get An Article', args: { area: this.area, id } })

            if (onPrintPage) {
                        this.article$ = this.store.pipe(select(selectAnArticleById(id)))

            }
        }
        return id
    }

    changeArticle(runningOrder: number) {
        this.article$ = this.store.pipe(
            select(selectAnArticleByRunningOrder(runningOrder))
        )
    }

    getNonEmptyColumns(arrayOfObjects: any[], columnsToReturn: string[] = []): string[] {
        let result: string[] = []
        const exactMinMaxRegex = /(.*)(_min|_max|_exact)$/
        if (arrayOfObjects && arrayOfObjects.length > 0) {
            let columnLookup: Dictionary<boolean> = {}
            for (const row of arrayOfObjects) {
                for (let key of Object.keys(row)) {
                    const value = row[key]

                    const matches = key.match(exactMinMaxRegex)

                    if (matches) {
                        columnLookup[matches[1]] = true
                    } else if (typeof value === 'number') {
                        columnLookup[key] = true
                    } else if (typeof value === 'string' && !isNaN(Number(value)) && !isNaN(parseFloat(value))) {
                        columnLookup[key] = true
                    } else if (typeof value === 'string') {
                        columnLookup[key] = (columnLookup[key] ?? false) || value.trim().length > 0
                    } else if (typeof value === 'object') {
                        if (Array.isArray(value)) {
                            columnLookup[key] = columnLookup[key] || value.length > 0
                        } else {
                            columnLookup[key] = true
                        }
                    }
                }
            }
            for (const key in columnLookup) {
                if (columnLookup[key]) {
                    result.push(key)
                }
            }
            if (columnsToReturn.length > 0) {
                result = result.filter((col) => columnsToReturn.includes(col))
            }
        }
        return result
    }
    getCreditorFormFromPetition(formName: string, petitionStructure: any[], pdfLink: string): any {
        let document: any = {
            id: 'xxx',
            file_extension: 'pdf',
            cdn_path: pdfLink
        }
        if (petitionStructure.length > 0) {
            for (const petitionPart of petitionStructure) {
                switch (petitionPart.part) {
                    case 'official form 204':
                        if (formName === 'form204') {
                            document.pageNumber = petitionPart.startPage
                            return document
                        }
                        break
                    case 'official form 206d':
                        if (formName === 'form206d') {
                            document.pageNumber = petitionPart.startPage
                            return document
                        }
                        break
                    case 'official form 206e/f':
                        if (formName === 'form206ef') {
                            document.pageNumber = petitionPart.startPage
                            return document
                        }
                        break
                }
            }
        }
        return null
    }

    trackbyArticleID(index: number, a: TTItem) {
        return a.id
    }
    getLinkedInHandle(str: string): string {
        try {
            const url = new URL(str)
            return url.pathname.replace('/in/', '').replace('/', '')
        } catch (e) {
            return ''
        }
    }
    trimURL(url: string) {
        let result = url.split('#')[0]
        result = result.split('?')[0]
        return result
    }

    sendAnalyticsForTabChange(tabID: string) {
        this.mixpanel.trackPageView({
            tabID,
            tab: this.sections[parseInt(tabID) - 1] ?? 'tab'
        })
    }
}